import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  EventEmitter,
  inject,
  OnDestroy,
  OnInit,
  Output,
  Signal,
  signal,
  ViewEncapsulation,
  WritableSignal
} from '@angular/core';
import { BodyComponent } from '@shared/components/body/body.component';
import { BoxComponent } from '@shared/components/box/box.component';
import { FooterComponent } from '@shared/components/footer/footer.component';
import { SelectDateComponent } from '@shared/components/form/select-date/select-date.component';
import { TextboxComponent } from '@shared/components/form/textbox/textbox.component';
import { HeaderComponent } from '@shared/components/header/header.component';
import { SelectionButtonComponent } from '@shared/components/selection-button/selection-button.component';

import { FormBuilder, ReactiveFormsModule, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Params } from '@angular/router';
import { ProfileState } from '@app/layout/common/profile/state/profile.state';
import { TranslocoPipe, TranslocoService } from '@ngneat/transloco';
import { PortActions } from '@vessel-reports/port/state/port.action';
import { PortState } from '@vessel-reports/port/state/port.state';
import { Button } from '@vessel-reports/port/state/port.types';
import { VesselReportsAction } from '@vessel-reports/state/vessel-reports.action';
import dayjs from 'dayjs';
import { isEmpty } from 'lodash';
import cloneDeep from 'lodash-es/cloneDeep';
import { Observable, Subject, take, takeUntil } from 'rxjs';
import { buttons } from '../../port.constants';

@Component({
  selector: 'create-port-report',
  imports: [
    BoxComponent,
    HeaderComponent,
    BodyComponent,
    FooterComponent,
    SelectDateComponent,
    TextboxComponent,
    SelectionButtonComponent,
    ReactiveFormsModule,
    TranslocoPipe
  ],
  templateUrl: './create-port-report.component.html',
  styleUrl: './create-port-report.component.scss',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CreatePortReportComponent implements OnInit, OnDestroy {
  protected readonly translocoService: TranslocoService = inject(TranslocoService);

  buttons: WritableSignal<Button[]> = signal(cloneDeep(buttons));
  @Output() save: EventEmitter<void> = new EventEmitter<void>(); // declare output
  // eslint-disable-next-line @angular-eslint/no-output-native
  @Output() close: EventEmitter<any> = new EventEmitter<any>();
  queryParams: WritableSignal<Params> = signal(void 0);
  form: UntypedFormGroup;
  // Injects
  private readonly _portReportState: PortState = inject(PortState);
  private readonly _portReportActions: PortActions = inject(PortActions);
  private readonly _profileState: ProfileState = inject(ProfileState);
  private readonly _portReportsActions: VesselReportsAction = inject(VesselReportsAction);
  private readonly _activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  private readonly _changeDetectorRef: ChangeDetectorRef = inject(ChangeDetectorRef);
  protected readonly isEmpty: typeof isEmpty = isEmpty;
  currentStatusIndex: string;
  previousNoonReport: Signal<any> = computed(() => this._portReportState.previousReport());
  private _unsubscribeAll: Subject<any> = new Subject<any>();

  constructor(private _formBuilder: FormBuilder) {
    this.form = this._formBuilder.group({
      report_type_id: ['', Validators.required],
      date_report: [dayjs().unix(), Validators.required],
      voyage_number: ['', Validators.required],
      vessel_id: [this._profileState.profile().ids_vessel_id, Validators.required]
    });
  }

  ngOnInit() {
    this._subscribeToSource$(this._activatedRoute.paramMap, (paramMap) => {
      this.currentStatusIndex = paramMap.get('status');
    });

    this._activatedRoute.queryParams.pipe(takeUntil(this._unsubscribeAll)).subscribe((queryParams) => {
      this.queryParams.set({ ...queryParams });
    });
  }

  ngOnDestroy() {
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  onClosePress: FunctionType = () => {
    this.close?.emit();
  };

  onDiscard: FunctionType = () => {
    this.close?.emit();
  };

  onSavePress: FunctionType = () => {
    const body = this.form.getRawValue();

    this._portReportActions.actions.createNewPortReport({
      body
    });
    this._portReportActions.actions.createNewPortReportSuccess$.pipe(take(1), takeUntil(this._unsubscribeAll)).subscribe(() => {
      this._portReportsActions.actions.getVesselReportPorts({
        queryParams: this.queryParams(),
        type: '6,8'
      });

      this.save.emit();
    });
  };

  onButtonsChange(buttons: Button[]) {
    const activeButton = buttons.find((button) => button.active);
    if (activeButton) {
      this.form.patchValue({
        report_type_id: activeButton.value
      });
    }
  }

  private _subscribeToSource$<T>(source$: Observable<T>, callback: (value: T) => void): void {
    source$.pipe(takeUntil(this._unsubscribeAll)).subscribe((value: T) => {
      callback(value);

      this._changeDetectorRef.markForCheck();
    });
  }
}
